import React, { useState } from "react";
import { Mail, MessageSquareText, LifeBuoy, CreditCard, Flag, Loader2, Check } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const CATEGORIES = [
  { id: "general", label: "General", icon: MessageSquareText, desc: "Questions, feedback, partnerships." },
  { id: "technical", label: "Tech Support", icon: LifeBuoy, desc: "App bugs, playback, account issues." },
  { id: "billing", label: "Billing", icon: CreditCard, desc: "Subscriptions, refunds, payments." },
  { id: "report", label: "Report Content", icon: Flag, desc: "Report abuse or policy violations." },
];

export default function Contact() {
  const { user } = useAuth();
  const [category, setCategory] = useState("general");
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !subject || !message) return toast.error("Email, subject, and message are required.");
    setSending(true);
    try {
      await api.post("/support/tickets", { name, email, subject, message, category });
      setSent(true);
      toast.success("Ticket submitted — we'll get back to you soon.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="contact-page" className="pt-32 pb-24 px-4 sm:px-8 md:px-12 max-w-5xl mx-auto">
      <div className="text-center">
        <div className="text-xs uppercase tracking-[0.3em] text-tv-accent font-bold">Contact us</div>
        <h1 className="font-display font-black text-5xl sm:text-6xl text-white mt-3 tracking-tighter">We're here to help.</h1>
        <p className="mt-3 text-white/70 max-w-2xl mx-auto">Tell us what's on your mind. Our team responds within one business day for tech + billing, and usually same-day for everything else.</p>
      </div>

      {sent ? (
        <div data-testid="contact-success" className="tv-glass rounded-2xl p-10 mt-10 text-center max-w-xl mx-auto border border-emerald-500/30">
          <div className="w-14 h-14 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto">
            <Check className="w-7 h-7 text-emerald-400" />
          </div>
          <h2 className="font-display font-black text-3xl text-white mt-5">Ticket submitted</h2>
          <p className="text-white/70 mt-2">We'll reach you at <b className="text-white">{email}</b> with a response shortly.</p>
          <button data-testid="contact-send-another" onClick={() => { setSent(false); setSubject(""); setMessage(""); }} className="mt-6 bg-tv-accent hover:bg-yellow-400 text-black font-bold px-5 py-2.5 rounded-full text-sm">
            Send another
          </button>
        </div>
      ) : (
        <form onSubmit={submit} data-testid="contact-form" className="mt-10 grid md:grid-cols-[280px_1fr] gap-6">
          <div className="space-y-2">
            <div className="text-xs uppercase tracking-widest text-white/50 font-bold mb-1">Category</div>
            {CATEGORIES.map((c) => {
              const I = c.icon;
              const selected = category === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setCategory(c.id)}
                  data-testid={`contact-category-${c.id}`}
                  className={`w-full text-left flex gap-3 items-start p-3 rounded-xl border transition ${selected ? "bg-tv-accent/10 border-tv-accent/50 ring-1 ring-tv-accent/30" : "bg-white/[0.02] border-white/10 hover:bg-white/5"}`}
                >
                  <I className={`w-5 h-5 ${selected ? "text-tv-accent" : "text-white/60"}`} />
                  <div>
                    <div className={`font-semibold text-sm ${selected ? "text-tv-accent" : "text-white"}`}>{c.label}</div>
                    <div className="text-[11px] text-white/50">{c.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="tv-glass rounded-2xl border border-white/10 p-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Your name</span>
                <input data-testid="contact-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm outline-none focus:border-tv-accent" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Email *</span>
                <input type="email" required data-testid="contact-email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm outline-none focus:border-tv-accent" />
              </label>
            </div>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Subject *</span>
              <input required data-testid="contact-subject" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={180} className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm outline-none focus:border-tv-accent" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Message *</span>
              <textarea required data-testid="contact-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={7} maxLength={4000} className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm outline-none focus:border-tv-accent resize-none" />
              <div className="text-[10px] text-white/40 mt-1">{message.length}/4000</div>
            </label>
            <div className="flex justify-end">
              <button
                type="submit"
                data-testid="contact-submit-btn"
                disabled={sending}
                className="bg-tv-accent hover:bg-yellow-400 disabled:opacity-50 text-black font-bold px-6 py-3 rounded-full flex items-center gap-2 text-sm"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                {sending ? "Sending…" : "Send message"}
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
